import { z } from "zod/v4";

const ToolCall = z.object({
  id: z
    .string()
    .min(1)
    .describe('Unique ID for this call within the plan, eg. "create-template"'),
  tool: z.string().min(1).describe("Name of the tool to call"),
  args: z
    .record(z.string(), z.unknown())
    .optional()
    .describe("The arguments you expect to pass to the tool"),
});
export type ToolCall = z.infer<typeof ToolCall>;

/** The name the MCP server serves a planned call's tool under. The agent may write the SDK's `mcp__<server>__` prefix. */
export function servedToolName(tool: string): string {
  return tool.replace(/^mcp__\w+?__/, "");
}

const Step = z.object({
  id: z
    .string()
    .min(1)
    .describe('Unique ID for this step within the plan, eg. "step-1"'),
  description: z
    .string()
    .min(1)
    .describe(
      'A short phrase naming the change and the object it acts on, eg. Pause the "Holiday promo" journey. Leave IDs and settings to the call args.',
    ),
  dependsOn: z
    .array(z.string())
    .optional()
    .describe("IDs of steps that must run before this one"),
  calls: z
    .array(ToolCall)
    .min(1)
    .describe("The tool calls this step makes, in the order they run"),
  context: z
    .array(z.string().min(1))
    .optional()
    .describe(
      "Paths of the context files you read whose guidance this step's tool calls follow",
    ),
});
export type Step = z.infer<typeof Step>;

export const ActionPlan = z
  .object({
    summary: z
      .string()
      .min(1)
      .describe(
        'The plan card\'s headline: a few plain words naming the outcome, eg. "Launch the fall win-back campaign". Leave names, dates, and settings to the steps.',
      ),
    steps: z.array(Step).min(1),
  })
  .superRefine(validateSteps);
export type ActionPlan = z.infer<typeof ActionPlan>;

function validateSteps(plan: { steps: Step[] }, ctx: z.RefinementCtx) {
  const stepIds = new Set<string>();
  const callLocations = new Map<string, { stepId: string; index: number }>();
  plan.steps.forEach((step, stepIndex) => {
    if (stepIds.has(step.id)) {
      ctx.addIssue({
        code: "custom",
        message: `Duplicate step id "${step.id}"`,
        path: ["steps", stepIndex, "id"],
      });
    }
    stepIds.add(step.id);
    step.calls.forEach((call, callIndex) => {
      if (callLocations.has(call.id)) {
        ctx.addIssue({
          code: "custom",
          message: `Duplicate call id "${call.id}"`,
          path: ["steps", stepIndex, "calls", callIndex, "id"],
        });
      }
      callLocations.set(call.id, { stepId: step.id, index: callIndex });
    });
  });

  plan.steps.forEach((step, stepIndex) => {
    for (const dependency of step.dependsOn ?? []) {
      if (!stepIds.has(dependency)) {
        ctx.addIssue({
          code: "custom",
          message: `Step "${step.id}" depends on unknown step "${dependency}"`,
          path: ["steps", stepIndex, "dependsOn"],
        });
      }
    }
  });

  const dependsOnById = new Map(
    plan.steps.map((step) => [step.id, step.dependsOn ?? []]),
  );
  // A step runs after every step it reaches through dependsOn, not only the ones it lists.
  const stepsRunBefore = (stepId: string): Set<string> => {
    const reached = new Set<string>();
    const pending = [...(dependsOnById.get(stepId) ?? [])];
    for (let next = pending.pop(); next !== undefined; next = pending.pop()) {
      if (!reached.has(next)) {
        reached.add(next);
        pending.push(...(dependsOnById.get(next) ?? []));
      }
    }
    return reached;
  };

  plan.steps.forEach((step, stepIndex) => {
    const earlierSteps = stepsRunBefore(step.id);
    step.calls.forEach((call, callIndex) => {
      const path = ["steps", stepIndex, "calls", callIndex, "args"];
      for (const segment of collectStrings(call.args).flatMap(
        splitReferences,
      )) {
        if (segment.kind !== "reference") {
          continue;
        }
        const referencedId = segment.target?.callId;
        const referenced = referencedId
          ? callLocations.get(referencedId)
          : undefined;
        if (!referencedId || !referenced) {
          ctx.addIssue({
            code: "custom",
            message: `Call "${call.id}" has invalid reference "${segment.placeholder}"; write references as \${{ calls.<id>.outputs.<field> }} to a call in this plan`,
            path,
          });
          continue;
        }
        const runsBefore =
          referenced.stepId === step.id
            ? referenced.index < callIndex
            : earlierSteps.has(referenced.stepId);
        if (!runsBefore) {
          ctx.addIssue({
            code: "custom",
            message: `Call "${call.id}" references call "${referencedId}", which must come earlier in step "${step.id}" or in a step it depends on`,
            path,
          });
        }
      }
    });
  });
}

// `${{ }}` marks a reference to another call's result; plain `{{ }}`, such as an email merge tag, is ordinary text.
const REFERENCE = /\$\{\{\s*(.*?)\s*\}\}/g;
const OUTPUTS_REFERENCE = /^calls\.(.+?)\.outputs\.(.+)$/;

export type ArgSegment =
  | { kind: "text"; text: string }
  | {
      kind: "reference";
      placeholder: string;
      /** Null when the placeholder is not a `calls.<id>.outputs.<field>` reference. */
      target: { callId: string; field: string } | null;
    };

/** Splits an argument string into plain text and `${{ }}` reference placeholders. */
export function splitReferences(text: string): ArgSegment[] {
  const segments: ArgSegment[] = [];
  let textStart = 0;
  for (const match of text.matchAll(REFERENCE)) {
    if (match.index > textStart) {
      segments.push({ kind: "text", text: text.slice(textStart, match.index) });
    }
    const [placeholder, expression = ""] = match;
    const [, callId, field] = OUTPUTS_REFERENCE.exec(expression) ?? [];
    segments.push({
      kind: "reference",
      placeholder,
      target: callId && field ? { callId, field } : null,
    });
    textStart = match.index + placeholder.length;
  }
  if (textStart < text.length) {
    segments.push({ kind: "text", text: text.slice(textStart) });
  }
  return segments;
}

function collectStrings(value: unknown): string[] {
  if (typeof value === "string") {
    return [value];
  }
  if (Array.isArray(value)) {
    return value.flatMap(collectStrings);
  }
  if (value !== null && typeof value === "object") {
    return Object.values(value).flatMap(collectStrings);
  }
  return [];
}
